import { HelmetProvider, Helmet } from 'react-helmet-async'
import { Route, Switch } from 'react-router-dom'
import { ThemeProvider } from 'theme-ui'
import React from 'react'

import { EVCoreProvider, RootEVEntry, GlobalStyles as Global } from '@uswitch/ev-core/react'
import * as EVCoreDefaultComponents from '@uswitch/ev-components/lib/components/defaults/components'
import { Options as EVCoreDefaultOptions } from '@uswitch/ev-components/lib/components/defaults/options'

import { useDataSelector, useEntity, useMeta } from '@uswitch/ev-utils/context/selectors'
import {
  robotsContent,
  canonicalHref,
} from '@uswitch/ev-components/lib/components/helpers/meta-tags'
import { cf2imgix } from '@uswitch/ev-components/lib/components/elements/imgix-img'
import { indexPages } from '@uswitch/ev-utils/helpers/env'
import ArticleSchema from '@uswitch/ev-components/lib/components/compounds/article-schema'
import BreadcrumbSchema from '@uswitch/ev-components/lib/components/compounds/breadcrumb-schema'

const MAX_TITLE_LENGTH = 70

const HeadContent = ({ meta, rootEntity }) => {
  const { breadcrumb, url } = useDataSelector()
  const linkedPage = useEntity(rootEntity.fields.link)
  const metaImage = useEntity(linkedPage.metaImage)
  const ogImage = useEntity(linkedPage.ogImage)
  const twitterAccount = useMeta('twitterAccount')
  const twitterImageParams = { auto: 'format,compress', w: '1200' }

  const titleStr = linkedPage.title || ''
  const title =
    titleStr.length <= MAX_TITLE_LENGTH
      ? [titleStr, meta.title].filter(i => i).join(' | ')
      : titleStr

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <link
          rel='icon'
          type={meta.favicon.type}
          href={meta.favicon.href}
          sizes={meta.favicon.sizes}
        />
        <link href={canonicalHref(url)} rel='canonical' />
        <link href='//uswitch-cms.imgix.net' rel='dns-prefetch' />
        <link href='https://uswitch-cms.imgix.net' rel='preconnect' crossorigin />
        <link href='//images.ctfassets.net' rel='dns-prefetch' />
        <link href='https://images.ctfassets.net' rel='preconnect' crossorigin />

        <Choose>
          <When condition={indexPages}>
            <meta name='robots' content={robotsContent(rootEntity.fields)} />
          </When>
          <Otherwise>
            <meta name='robots' content='noindex' />
          </Otherwise>
        </Choose>
        <meta name='description' content={linkedPage.metaDescription} />
        <meta property='og:title' content={linkedPage.ogTitle || title} />
        <meta property='og:type' content={linkedPage.ogType || 'website'} />
        <meta
          property='og:description'
          content={linkedPage.ogDescription || linkedPage.metaDescription}
        />
        <meta property='og:url' content={canonicalHref(url)} />
        <Choose>
          <When condition={ogImage.sys}>
            <meta property='og:image' content={cf2imgix(ogImage.file.url)} />
            <meta property='og:image:secure_url' content={cf2imgix(ogImage.file.url)} />
          </When>
          <When condition={metaImage.sys}>
            <meta property='og:image' content={cf2imgix(metaImage.file.url)} />
            <meta property='og:image:secure_url' content={cf2imgix(metaImage.file.url)} />
          </When>
        </Choose>
        <If condition={twitterAccount}>
          <meta property='twitter:card' content='summary_large_image' />
          <meta property='twitter:site' content={twitterAccount} />
          <Choose>
            <When condition={ogImage.sys}>
              <meta
                property='twitter:image'
                content={cf2imgix(ogImage.file.url, twitterImageParams)}
              />
            </When>
            <When condition={metaImage.sys}>
              <meta
                property='twitter:image'
                content={cf2imgix(metaImage.file.url, twitterImageParams)}
              />
            </When>
          </Choose>
        </If>
      </Helmet>
      <BreadcrumbSchema breadcrumb={breadcrumb} url={url} />
      <ArticleSchema page={linkedPage} url={rootEntity.fields} meta={meta} />
    </>
  )
}

const App = ({ isServer, initialState, helmetContext = {}, experiences: __experiences }) => {
  const state = isServer ? initialState : window.__initialState__
  const experiences = isServer ? __experiences : window.__experiences__
  const { theme, evCoreState } = state

  return (
    <HelmetProvider context={helmetContext}>
      <EVCoreProvider
        data={evCoreState}
        components={EVCoreDefaultComponents}
        options={EVCoreDefaultOptions}
        tracking={
          isServer
            ? undefined
            : {
                client: window.nerdalytics,
              }
        }
        experiences={experiences}
      >
        <ThemeProvider theme={theme}>
          <Global />
          <Switch>
            <Route
              path='/'
              render={() => {
                return (
                  <>
                    <HeadContent rootEntity={evCoreState.rootEntity} meta={evCoreState.meta} />
                    <RootEVEntry {...evCoreState.rootEntity} />
                  </>
                )
              }}
            />
          </Switch>
        </ThemeProvider>
      </EVCoreProvider>
    </HelmetProvider>
  )
}

export default App
